import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Header from '../components/newHeader';
import Footer from '../components/Footer';
import FormRatingTrendChart from '../components/FormRatingTrendChart';
import StudentInformation from '../components/Home/StudentInfomation';
import TestCard from '../components/TestCard';
import { HelpCircle, Crown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

function HomeV3() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTest] = useState(null);
  const [studentInfoState, setStudentInfoState] = useState(null);
  const [latestReports, setLatestReports] = useState([]);
  const [displayCount, setDisplayCount] = useState(3);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/student/home/v3`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        );
        setData(response.data);

        // v3에서 학생 정보 추출 시도
        const v3 = response?.data?.data;
        const studentInfo = v3?.student_info;
        
        if (studentInfo) {
          setStudentInfoState({
            student_name: studentInfo.student_name ?? '',
            school_name: studentInfo.school_name ?? '',
            student_grade: studentInfo.student_grade ?? ''
          });
        } else {
          // v3에 없으면 v2에서 최소 정보 보강
          try {
            const v2 = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/student/home/v2`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              }
            );
            const d = v2?.data?.data;
            if (d) {
              setStudentInfoState({
                student_name: d.student_name ?? '',
                school_name: d.school_name ?? '',
                student_grade: (d.student_grade || d.school_grade) ?? ''
              });
            }
          } catch (_) {
            // ignore; 카드가 placeholder로만 표시될 수 있음
          }
        }
        
        // 최신 리포트 데이터 설정 (예시 데이터)
        setLatestReports([
          { title: '2024 3월 모의고사', date: '2024.03.15', accuracy: 85, difficulty: '상', questionCount: 40 },
          { title: '2024 2월 월말평가', date: '2024.02.28', accuracy: 78, difficulty: '중', questionCount: 30 },
          { title: '2024 1월 모의고사', date: '2024.01.20', accuracy: 82, difficulty: '중', questionCount: 35 },
        ]);
      } catch (err) {
        setError({
          status: err.response?.status || 0,
          message: err.response?.data?.message || err.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // v3 응답에서 차트용 데이터 구성
  const testHistory = useMemo(() => {
    const stamps = data?.data?.stamps || [];
    const tests = [];

    for (const stamp of stamps) {
      const exams = Array.isArray(stamp.exams) ? stamp.exams : [];
      for (const exam of exams) {
        let totalQuestions = 0;
        let correctCount = 0;

        if (exam.by_step) {
          for (let level = 1; level <= 5; level++) {
            const stepData = exam.by_step[String(level)];
            if (Array.isArray(stepData) && stepData.length >= 2) {
              const [totalProblems, correct] = stepData;
              totalQuestions += totalProblems || 0;
              correctCount += correct || 0;
            }
          }
        } else if (Array.isArray(exam.problems) && exam.result) {
          totalQuestions = exam.problems.length;
          correctCount = Object.values(exam.result).filter(Boolean).length;
        } else if (typeof exam.length === 'number') {
          totalQuestions = exam.length;
        }

        const wrongCount = totalQuestions > 0 ? totalQuestions - correctCount : 0;

        tests.push({
          ...exam,
          stamp_id: stamp.stamp_id,
          tree_name: stamp.tree_name,
          edu_ver: stamp.edu_ver,
          correct_count: correctCount,
          wrong_count: wrongCount
        });
      }
    }

    // Sort by updated_at or start_time in descending order (newest first)
    return tests.sort((a, b) => {
      const dateA = new Date(a.updated_at || a.start_time || 0).getTime();
      const dateB = new Date(b.updated_at || b.start_time || 0).getTime();
      return dateB - dateA;
    });
  }, [data]);


  // 테스트 선택 핸들러
  const handleTestSelect = (test) => {
    if (test.exam_id) {
      navigate('/kytest');
    }
  };

  const reportData = useMemo(() => {
    const exams = (data?.data?.stamps || [])
      .flatMap(stamp => (stamp.exams || []))
      .filter(e => e && e.exam_id != null)
      .map(e => ({ exam_id: e.exam_id, rating: e.rating ?? null, delta: e.delta ?? 0 }));
    return { exams };
  }, [data]);

  const studentInfo = useMemo(() => {
    const v3 = data?.data;
    if (!v3 || !v3.student_info) return null;
    
    const info = v3.student_info;
    return {
      student_name: info.student_name ?? '',
      school_name: info.school_name ?? '',
      student_grade: info.student_grade ?? ''
    };
  }, [data]);

  return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />
      <div className="relative z-20 flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow p-4" style={{ 
          paddingTop: 'calc(env(safe-area-inset-top) + 76px)',
          paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px))'
        }}>
          {loading && (
            <div className="flex flex-col items-center py-10">
              <div className="w-12 h-12 border-4 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-700 font-medium animate-pulse">로딩중...</p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 text-red-800 p-4 rounded-md">
              <p><strong>Error</strong></p>
              <p>Status: {error.status}</p>
              <p>Message: {error.message}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="max-w-[700px] w-full mx-auto">
              <div className="mb-4">
                <StudentInformation studentInfo={studentInfo || studentInfoState || { student_name: '-', school_name: '-', student_grade: '-' }} />
              </div>
              
              {/* Header section with 학습 상태 and 전체 랭킹 */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <p className="text-[20px] font-bold text-[#231F20]">
                    학습 상태
                  </p>
                  <HelpCircle className="w-[15px] h-[15px] text-[#A3A1A2]" />
                </div>
                <Link 
                  to="/ranking" 
                  className="flex items-center gap-1 px-3 h-[20px] border border-[#A3A1A2] rounded-[8px] hover:opacity-80"
                >
                  <Crown className="w-3 h-3 text-[#A3A1A2]" />
                  <span className="text-[10px] font-medium text-[#A3A1A2]">전체 랭킹</span>
                </Link>
              </div>
              
              <div className="mt-2">
                <FormRatingTrendChart
                  selectedTest={selectedTest}
                  testHistory={testHistory}
                  reportData={reportData}
                  onTestSelect={handleTestSelect}
                />
              </div>
              

              {/* 최신 리포트 헤더 */}
              <div className="flex items-center gap-2 mt-4">
                <p className="text-[20px] font-bold text-[#231F20]">
                  최신 리포트
                </p>
                <HelpCircle className="w-[15px] h-[15px] text-[#A3A1A2]" />
              </div>

              {/* 테스트 히스토리 카드 목록 */}
              <div className="mt-2 space-y-3">
                {testHistory
                  .filter(test => test.report_info?.is_closed === true) // Only show cards with ready reports
                  .slice(0, displayCount)
                  .map((test, index) => {
                    const isReportReady = test.report_info?.is_closed === true;

                    return (
                      <TestCard
                        key={`${test.stamp_id}-${test.exam_id}-${index}`}
                        testData={test}
                        index={index}
                        isClickable={isReportReady}
                        isReportReady={isReportReady}
                        onClick={() => {
                          if (isReportReady) {
                            navigate(`/myresult/${test.exam_id}/report`);
                          }
                        }}
                      />
                    );
                  })}

                {testHistory.filter(test => test.report_info?.is_closed === true).length > displayCount && (
                  <button
                    onClick={() => setDisplayCount(prev => prev + 3)}
                    className="w-full py-3 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    더 보기 ({Math.min(3, testHistory.filter(test => test.report_info?.is_closed === true).length - displayCount)}개 더)
                  </button>
                )}
              </div>
            </div>
          )}

        </main>
        <Footer />
      </div>
    </div>
  );
}

export default HomeV3;


